import { Col } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import { useContext, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import VpnMacConnected from "@/assets/images/vpnMacConnected.png";
import VpnMacEnterPin from "@/assets/images/vpnMacEnterPin.png";
import VpnMacEnterUsername from "@/assets/images/vpnMacEnterUsername.png";
import VpnMacSelectGroup from "@/assets/images/vpnMacSelectGroup.png";
import VpnPressWinKey from "@/assets/images/vpnPressWinKey.png";
import VpnReadyToConnect from "@/assets/images/vpnReadyToConnect.png";
import VpnWinEnterPass from "@/assets/images/vpnWinEnterPass.png";
import VpnWinLaunch from "@/assets/images/vpnWinLaunch.png";
import VpnWinSecureConnect from "@/assets/images/vpnWinSecureConnect.png";
import VpnWinSelectGroup from "@/assets/images/vpnWinSelectGroup.png";
import ImagesRow from "@/components/Commons/ImagesRow";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { STEP_ID, StepsList } from "@/components/constants";
import { getOS, OPERATING_SYSTEM } from "@/helpers/os-helper";
import { useCheckVPNConnectivity } from "@/hooks/useCheckVPNConnectivity";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";

const FONT_WEIGHT = "regular";

/**
 * The steps of connecting to VPN on a macOS machine.
 */
const MAC_OS_VPN_STEPS: StepsList[] = [
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step1`,
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step2`,
    image: {
      src: VpnReadyToConnect,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.MAC}-step2-altText`,
      widthPx: 438,
      heightPx: 215,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step3`,
    image: {
      src: VpnMacSelectGroup,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.MAC}-step3-altText`,
      widthPx: 639,
      heightPx: 322,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step4`,
    image: {
      src: VpnMacEnterUsername,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.MAC}-step4-altText`,
      widthPx: 515,
      heightPx: 190,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step5`,
    image: {
      src: VpnMacEnterPin,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.MAC}-step5-altText`,
      widthPx: 635,
      heightPx: 228,
    },
    children: (
      <Col padding={{ top: "S300" }}>
        <MessageBanner
          dataTestId="connectVpnStep5Warning"
          type={MessageBannerType.Warning}
        >
          <Text>
            <FormattedMessage
              id={`connectVpn-expander-body-${OPERATING_SYSTEM.MAC}-step5-warning`}
              values={{
                keyPinText: "Security key PIN + Press security key",
                passwordText: "Password",
              }}
            />
          </Text>
        </MessageBanner>
      </Col>
    ),
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.MAC}-step6`,
    image: {
      src: VpnMacConnected,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.MAC}-step6-altText`,
      widthPx: 261,
      heightPx: 313,
    },
  },
];

/**
 * The steps of connecting to VPN on a Windows machine.
 */
const WINDOWS_VPN_STEPS: StepsList[] = [
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step1`,
    image: {
      src: VpnPressWinKey,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.WINDOWS}-step1-altText`,
      widthPx: 317,
      heightPx: 319,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step2`,
    image: {
      src: VpnWinLaunch,
      altTextLocaleId: `connectSecureVpn-image-${OPERATING_SYSTEM.WINDOWS}-step2-altText`,
      widthPx: 360,
      heightPx: 119,
    },
    children: (
      <Col padding={{ top: "S300" }}>
        <MessageBanner
          dataTestId="connectVpnStep5Warning"
          type={MessageBannerType.Warning}
        >
          <Text>
            <FormattedMessage
              id={`connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step2-note`}
              values={{
                ciscoText: "Cisco Secure Client",
              }}
            />
          </Text>
        </MessageBanner>
      </Col>
    ),
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step3`,
    image: {
      src: VpnWinSecureConnect,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.WINDOWS}-step3-altText`,
      widthPx: 372,
      heightPx: 253,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step4`,
    image: {
      src: VpnWinSelectGroup,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.WINDOWS}-step4-altText`,
      widthPx: 372,
      heightPx: 253,
    },
  },
  {
    stepLocaleId: `connectSecureVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step5`,
  },
  {
    stepLocaleId: `connectVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step6`,
    image: {
      src: VpnWinEnterPass,
      altTextLocaleId: `connectVpn-image-${OPERATING_SYSTEM.WINDOWS}-step6-altText`,
      widthPx: 364,
      heightPx: 224,
    },
  },
  {
    stepLocaleId: `connectVpn-expander-body-${OPERATING_SYSTEM.WINDOWS}-step7`,
  },
];

/**
 * The detail steps needed to connect to the VPN.
 */
const VpnStepsList = (): JSX.Element => {
  const stepsList =
    getOS() === OPERATING_SYSTEM.MAC ? MAC_OS_VPN_STEPS : WINDOWS_VPN_STEPS;

  return (
    <>
      {stepsList.map((step, index) => (
        <Text
          className="stepList-text"
          key={step.stepLocaleId}
          fontWeight={FONT_WEIGHT}
          dataTestId={`connectVpnStep${index + 1}`}
        >
          <li>
            <FormattedMessage
              id={step.stepLocaleId}
              values={{
                ciscoText: "Cisco Secure Client",
                groupText: "Group",
                orcaText: "orca-Amazon-Onboarding",
                usernameText: "Username",
                keyPinText: "Security key PIN + Press security key",
                passwordText: "Password",
              }}
            />
            {step.image ? (
              <Col padding={{ top: "S300" }}>
                <ImagesRow images={[step.image]} rowPadding="S400" />
              </Col>
            ) : null}
            {step.children}
          </li>
        </Text>
      ))}
    </>
  );
};

/**
 * A step to help NHs to connect to the Amazon network through VPN.
 */
export default function ConnectVpn(): JSX.Element {
  const { dispatch } = useContext(AppContext);
  const { data: isConnectedToVpn } = useCheckVPNConnectivity();
  const isMac = getOS() === OPERATING_SYSTEM.MAC;

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-connect-to-vpn",
    });
  }, []);

  /**
   * Dispatch action to enable/disable the next button depending on the status of VPN connection.
   */
  useEffect(() => {
    if (isConnectedToVpn) {
      dispatch({
        stepId: STEP_ID.CONNECT_VPN,
        type: ActionType.SET_NEXT_BTN_STATUS,
        enableNext: true,
      });
      dispatch({ type: ActionType.UPDATE_NETWORK, isNetworkConnected: true });
    } else {
      dispatch({
        stepId: STEP_ID.CONNECT_VPN,
        type: ActionType.SET_NEXT_BTN_STATUS,
        enableNext: false,
        disabledBtnToolTipI18nId:
          "connectVpn-button-tooltip-vpnNotConnectedHint",
      });
    }
  }, [dispatch, isConnectedToVpn]);

  return (
    <Col gridGap="S400">
      <StepBodyStyledText>
        <Text fontWeight={FONT_WEIGHT} dataTestId="connectVpnIntroText">
          <FormattedMessage id="connectVpn-expander-body-intro" />
        </Text>
        <Text fontWeight={FONT_WEIGHT}>
          <ol className="stepLists">
            <VpnStepsList />
          </ol>
        </Text>
      </StepBodyStyledText>
    </Col>
  );
}
